import {Loader, dataTestId} from '@hconnect/uikit'
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack
} from '@mui/material'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useAreaTypes} from '../hooks/upmEntityConfig/useAreaTypes'
import {useCreateArea} from '../hooks/upmEntityConfig/useCreateArea'
import {useItemDetails} from '../providers'
import {AreaCreateType} from '../types/departmentConfig'

interface AddAreaPopupProps {
  upmPlantId: string
  existingAreas: string[]
}

export const AddAreaPopup: React.FC<AddAreaPopupProps> = ({upmPlantId, existingAreas}) => {
  const {
    handleSubmit,
    control,
    formState: {isValid}
  } = useForm<AreaCreateType>({
    mode: 'onChange',
    defaultValues: {
      typeId: '',
      plantId: ''
    }
  })

  const {setAddArea} = useItemDetails()
  const handleClose = () => {
    setAddArea(null)
  }

  const {data: areaTypes, isLoading: isLoadingAreaTypes} = useAreaTypes()

  const filteredAreas = areaTypes?.filter(
    (areaType) =>
      !existingAreas.find(
        (existingAreaType) => `${areaType.code}_${areaType.name}` === existingAreaType
      )
  )

  const {mutate: createArea} = useCreateArea()

  const {t} = useTranslation()

  const onSubmit = (data: AreaCreateType) => {
    createArea(
      {...data, plantId: upmPlantId},
      {
        onSuccess: () => {
          handleClose()
        }
      }
    )
  }

  const noAreaTypes = !areaTypes || areaTypes.length === 0

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('plantSetup.departmentConfig.addNewArea')}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {isLoadingAreaTypes ? (
            <Loader />
          ) : (
            <Stack gap={2}>
              <Controller
                name="typeId"
                control={control}
                rules={{required: t('error.required')}}
                render={({field, fieldState}) => (
                  <TextField
                    {...field}
                    select
                    label={t('plantSetup.departmentConfig.areaType')}
                    {...dataTestId('area-type-select')}
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    disabled={noAreaTypes || isLoadingAreaTypes}
                  >
                    {filteredAreas?.map((area) => (
                      <MenuItem
                        key={area.id}
                        value={area.id}
                        {...dataTestId(`area-type-option-${area.id}`)}
                      >
                        {area.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            {...dataTestId('save-area-btn')}
            disabled={!isValid || noAreaTypes || isLoadingAreaTypes}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
